import {
    HeartIcon as CentralHeartIcon,
    StarFilledIcon as CentralStarFilledIcon,
    ChevronSmallRightIcon as CentralChevronSmallRightIcon,
} from "@wags/ui/product-icons.tsx";
import { Link } from "@tanstack/react-router";
import { ProviderType } from "@wags/catalog/pure.ts";
import { cn } from "@wags/ui/ui-utils";
import { VercelImage } from "@wags/ui/vercel-image.tsx";
import { PawGlyph, type Highlight } from "@/features/providers";
import { joinParts, type ListingData, PinGlyph } from "@/features/listings";
import { VerifiedProviderBadge } from "@/components/shared/VerifiedProviderBadge";

const GLYPH_CLASS = "mt-0.5 size-4 shrink-0 text-doberman-black/60";

export function hasBreederContent(
    data: ListingData,
    highlights?: Highlight[],
): boolean {
    return Boolean(
        data.providerName ||
        joinParts([data.city, data.distanceLabel]) ||
        highlights?.length,
    );
}

function HeartIcon() {
    return <CentralHeartIcon aria-hidden="true" className={GLYPH_CLASS} />;
}

function HighlightGlyph({ icon }: { icon: Highlight["icon"] }) {
    if (icon === "location") return <PinGlyph className={GLYPH_CLASS} />;
    if (icon === "rearing") return <PawGlyph className={GLYPH_CLASS} />;
    return <HeartIcon />;
}

function StarIcon() {
    return (
        <CentralStarFilledIcon
            aria-hidden="true"
            className="size-4 shrink-0 text-labrador-yellow"
        />
    );
}

function ChevronIcon({ className }: { className?: string }) {
    return (
        <CentralChevronSmallRightIcon
            aria-hidden="true"
            className={className}
        />
    );
}

const FOOTER_LINK_ROW_CLASS =
    "mt-4 flex w-full items-center justify-between rounded-nested bg-true-black/[0.03] px-4 py-3.5 text-left transition-colors [@media(hover:hover)]:hover:bg-true-black/[0.06]";

function FooterLinkRow({
    label,
    providerId,
}: {
    label: string;
    providerId?: string;
}) {
    const inner = (
        <>
            <span className="text-label text-doberman-black">{label}</span>
            <ChevronIcon className="size-5 shrink-0 text-doberman-black/40" />
        </>
    );

    if (!providerId) {
        return (
            <button type="button" className={FOOTER_LINK_ROW_CLASS}>
                {inner}
            </button>
        );
    }

    return (
        <Link
            to="/provider/$providerId"
            params={{ providerId }}
            className={FOOTER_LINK_ROW_CLASS}
        >
            {inner}
        </Link>
    );
}

export function ProviderHighlights({
    highlights,
}: {
    highlights: Highlight[] | undefined;
}) {
    if (!highlights?.length) return null;
    return (
        <div className="mt-4 flex flex-col gap-3">
            {highlights.map((h) => (
                <div key={h.text} className="flex items-start gap-2.5">
                    <HighlightGlyph icon={h.icon} />
                    <p className="text-body-small text-doberman-black/80">
                        {h.text}
                    </p>
                </div>
            ))}
        </div>
    );
}

type Stat = { value: string; label: string; star?: boolean };

function ProviderSummary({
    full,
    avatarUrl,
    name,
    isVerified,
    providerType,
    defaultName,
    place,
    slimSupport,
    stats,
}: {
    full: boolean;
    avatarUrl?: string;
    name?: string;
    isVerified?: boolean;
    providerType: ProviderType;
    defaultName: string;
    place?: string;
    slimSupport?: string | null;
    stats: Stat[];
}) {
    if (!full) {
        return (
            <div className="flex flex-col items-center rounded-nested bg-doberman-black/[0.03] px-4 py-6 text-center">
                {avatarUrl && (
                    <div className="img-inset size-17 shrink-0 overflow-hidden rounded-full">
                        <VercelImage
                            src={avatarUrl}
                            alt={name ?? "The provider"}
                            width={136}
                            height={136}
                            className="h-full w-full object-cover"
                        />
                    </div>
                )}
                <p
                    className={cn(
                        "text-item-title text-doberman-black text-pretty",
                        avatarUrl && "mt-2.5",
                    )}
                >
                    {name ?? defaultName}
                    <VerifiedProviderBadge
                        isVerified={isVerified}
                        providerType={providerType}
                    />
                </p>
                {slimSupport && (
                    <p className="mt-0.5 text-caption text-doberman-black/55">
                        {slimSupport}
                    </p>
                )}
            </div>
        );
    }
    return (
        <div className="mb-4 flex flex-col items-center rounded-nested bg-doberman-black/[0.03] px-4 py-5 text-center">
            {avatarUrl && (
                <div className="img-inset size-19 shrink-0 overflow-hidden rounded-full">
                    <VercelImage
                        src={avatarUrl}
                        alt={name ?? "The provider"}
                        width={152}
                        height={152}
                        className="h-full w-full object-cover"
                    />
                </div>
            )}
            <p
                className={cn(
                    "text-item-title text-doberman-black text-pretty",
                    avatarUrl && "mt-2.5",
                )}
            >
                {name ?? defaultName}
                <VerifiedProviderBadge
                    isVerified={isVerified}
                    providerType={providerType}
                />
            </p>
            {place && (
                <p className="mt-0.5 text-caption text-doberman-black/55">
                    {place}
                </p>
            )}
            <div className="mt-4 flex items-center gap-4">
                {stats.map((stat, i) => (
                    <div key={stat.label} className="flex items-center gap-4">
                        {i > 0 && (
                            <div className="h-8 w-px bg-doberman-black/10" />
                        )}
                        <div className="flex flex-col items-center">
                            <p className="flex items-center gap-1 text-price text-doberman-black">
                                {stat.value}
                                {stat.star && <StarIcon />}
                            </p>
                            <p className="text-caption text-doberman-black/55">
                                {stat.label}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function BreederSection({
    data,
    avatarUrl,
    reviewCount,
    sinceYear,
    rating,
    ratingCount,
    highlights,
    providerKind = "breeder",
}: {
    data: ListingData;

    avatarUrl?: string;
    reviewCount?: number | null;
    sinceYear?: number | null;

    rating?: number | null;

    ratingCount?: number | null;

    highlights?: Highlight[];

    providerKind?: "breeder" | "shelter";
}) {
    const isShelter = providerKind === "shelter";
    const defaultName = isShelter ? "The rescue" : "The breeder";
    const name = data.providerName;
    const place = joinParts([data.city, data.distanceLabel]);
    const linkLabel = name ? `More about ${name}` : "See the full profile";

    if (!hasBreederContent(data, highlights)) return null;

    const years = sinceYear ? new Date().getFullYear() - sinceYear : 0;
    const stats: Stat[] = [
        reviewCount && reviewCount > 0
            ? {
                  value: String(reviewCount),
                  label: reviewCount === 1 ? "Review" : "Reviews",
              }
            : null,
        rating != null && ratingCount != null && ratingCount > 0
            ? {
                  value: rating.toFixed(1),
                  label: `${ratingCount} ${ratingCount === 1 ? "rating" : "ratings"}`,
                  star: true,
              }
            : null,
        years > 0 ? { value: `${years} yrs`, label: "in business" } : null,
    ].filter((s): s is Stat => s !== null);

    const full = stats.length >= 2;
    const slimMeta = [
        place,
        stats[0] ? `${stats[0].value} ${stats[0].label.toLowerCase()}` : null,
    ]
        .filter(Boolean)
        .join(" · ");

    const slimSupport =
        slimMeta || (isShelter ? "Rescue & adoption organization" : null);

    return (
        <>
            <ProviderSummary
                full={full}
                avatarUrl={avatarUrl}
                name={name}
                isVerified={data.providerIsVerified}
                providerType={
                    isShelter
                        ? ProviderType.ShelterRescue
                        : ProviderType.Breeder
                }
                defaultName={defaultName}
                place={place}
                slimSupport={slimSupport}
                stats={stats}
            />
            <ProviderHighlights highlights={highlights} />
            <FooterLinkRow label={linkLabel} providerId={data.providerId} />
        </>
    );
}
